/**
 * Port rules shared by the node-graph schemas (Logic, Home Assistant).
 *
 * An output port `provides` exactly one kind; an input port `accepts` a list
 * of kinds and may cap its number of wires with `max`. Every connection rule
 * that does not need graph state is derived from these declarations.
 */
export function isPortCompatible(output, input) {
    return typeof output?.provides === 'string' &&
        Array.isArray(input?.accepts) && input.accepts.includes(output.provides)
}

const NO_PORTS = Object.freeze({ inputs: [], outputs: [] })

/**
 * Ports of a graph node: its schema's `portsFor(data)` when the ports depend
 * on the node's data (e.g. one output per switch case), else `ports`.
 */
export function resolveNodePorts(getNodeSchema, node) {
    const schema = getNodeSchema(node?.type)
    return schema?.portsFor?.(node.data ?? {}) ?? schema?.ports ?? NO_PORTS
}

/**
 * Palette groups narrowed to nodes that could take an open wire end: for a
 * wire drawn from an output (`dir: 'out'`) a node whose input accepts `port`;
 * for a wire drawn from an input (`dir: 'in'`) a node whose output `port`
 * accepts.
 */
export function filterPaletteForWire(palette, getNodeSchema, dir, port) {
    const fits = entry => {
        const ports = getNodeSchema(entry.nodeType)?.ports
        return dir === 'out'
            ? (ports?.inputs ?? []).some(input => isPortCompatible(port, input))
            : (ports?.outputs ?? []).some(output => isPortCompatible(output, port))
    }
    return palette
        .map(group => ({ ...group, entries: group.entries.filter(fits) }))
        .filter(group => group.entries.length > 0)
}

/** Output port of a switch case; `else` takes what no case matches (Logic and Home Assistant switches). */
export const SWITCH_ELSE_PORT = 'else'
export const switchCasePort = caseId => `case-${caseId}`

/** Short label of a switch case, e.g. "== off". */
export function describeSwitchCase(item) {
    return `${item?.operator ?? '=='} ${item?.value ?? ''}`.trim()
}
